import { z } from "zod";

/**
 * Parses `items` element-by-element with `schema`, dropping (and logging)
 * rows that fail instead of throwing the whole payload away. These arrays
 * are fed straight into panel state by 5s pollers, so a whole-array
 * `.parse` would freeze every cell behind a single malformed row and
 * re-toast the failure on every tick (MS-3366) — one bad row must degrade
 * only itself.
 *
 * Only ROWS are tolerated. A non-array payload is a broken envelope, and
 * callers commit this helper's result as their next snapshot: returning `[]`
 * would replace live run state with "no runs" while the fetch looked
 * successful. It throws instead, so the caller's error path runs exactly as a
 * whole-payload `.parse` failure did before.
 */
export function parseModelArrayTolerant<Schema extends z.ZodTypeAny>(
  schema: Schema,
  items: unknown,
  label: string,
): z.output<Schema>[] {
  if (!Array.isArray(items)) {
    throw new Error(`Expected an array of ${label}, got ${typeof items}`);
  }
  const parsed: z.output<Schema>[] = [];
  for (const item of items) {
    const result = schema.safeParse(item);
    if (result.success) {
      parsed.push(result.data);
    } else {
      console.error(
        `Dropping ${label} row that failed schema parse:`,
        result.error.message,
      );
    }
  }
  return parsed;
}
